import { type ReactNode, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  type AdminProductListItem,
  type BannerLink,
  type CategoryTreeResponse,
  BANNER_SEARCH_MAX,
  formatBannerLink,
  parseBannerLink,
  pickLocalized,
} from '@aps/contracts';
import { Button, formatMoney } from '@aps/ui';
import { api } from '../lib/api.js';
import { messages } from '../i18n/messages.js';
import { useSession } from '../app/session-context.js';

export type BannerLinkType = 'none' | BannerLink['type'];

/** Forma holati: tur va nishon (tovar slug'i, kategoriya id'si yoki qidiruv matni). */
export interface BannerLinkDraft {
  linkType: BannerLinkType;
  linkTarget: string;
}

export const EMPTY_LINK_DRAFT: BannerLinkDraft = { linkType: 'none', linkTarget: '' };

/** Saqlangan `linkUrl` → forma holati. Noto'g'ri/yo'q qiymat — "havolasiz". */
export function linkDraftOf(linkUrl: string | null): BannerLinkDraft {
  const link = parseBannerLink(linkUrl);
  if (link === null) return EMPTY_LINK_DRAFT;
  switch (link.type) {
    case 'product':
      return { linkType: 'product', linkTarget: link.slug };
    case 'category':
      return { linkType: 'category', linkTarget: link.categoryId };
    case 'search':
      return { linkType: 'search', linkTarget: link.query };
    case 'catalog':
      return { linkType: 'catalog', linkTarget: '' };
    default: {
      const exhaustive: never = link;
      return exhaustive;
    }
  }
}

/**
 * Forma holati → `linkUrl`. `incomplete` — tur tanlangan, lekin nishon yo'q yoki noto'g'ri:
 * bunda saqlash bloklanadi (jimgina "havolasiz" saqlanib qolmasin).
 */
export function linkUrlOf(draft: BannerLinkDraft): { url: string | null; incomplete: boolean } {
  if (draft.linkType === 'none') return { url: null, incomplete: false };
  const url =
    draft.linkType === 'catalog' ? 'catalog' : `${draft.linkType}:${draft.linkTarget.trim()}`;
  const link = parseBannerLink(url);
  return link === null
    ? { url: null, incomplete: true }
    : { url: formatBannerLink(link), incomplete: false };
}

/** Artikul AYNAN mos kelgan tovar birinchi: operator artikulni yozsa uni qidiruv shovqini ko'mmasin. */
function sortExactSkuFirst(items: AdminProductListItem[], term: string): AdminProductListItem[] {
  const needle = term.trim().toLowerCase();
  const exact = items.filter((item) => item.sku.toLowerCase() === needle);
  return [...exact, ...items.filter((item) => item.sku.toLowerCase() !== needle)];
}

function flatten(
  nodes: CategoryTreeResponse[],
  depth = 0,
): { id: string; name: (locale: 'UZ' | 'RU') => string; depth: number }[] {
  return nodes
    .filter((node) => node.isActive)
    .flatMap((node) => [
      {
        id: node.id,
        name: (locale: 'UZ' | 'RU') => pickLocalized(node.nameUz, node.nameRu, locale),
        depth,
      },
      ...flatten(node.children, depth + 1),
    ]);
}

/**
 * Banner havolasi: "bosilganda qayerga olib boradi". Tur tanlanadi, so'ng nishon:
 * tovar (qidirib topiladi — faqat faol tovarlar), kategoriya (daraxtdan), qidiruv matni.
 */
export function BannerLinkField({
  value,
  onChange,
  onFillFromProduct,
}: {
  value: BannerLinkDraft;
  onChange: (next: BannerLinkDraft) => void;
  /** Tovar tanlangach "Tovardan to'ldirish" tugmasi chiqadi (banner matnini tovardan oladi). */
  onFillFromProduct?: (product: AdminProductListItem) => void;
}): ReactNode {
  const { locale } = useSession();
  const t = messages(locale);
  const [term, setTerm] = useState('');
  // Shu sessiyada tanlangan tovar: slug emas, nom va artikul ko'rsatish uchun.
  const [chosen, setChosen] = useState<AdminProductListItem | null>(null);

  const categories = useQuery({
    queryKey: ['category-tree'],
    queryFn: () => api.categoryTree(),
    enabled: value.linkType === 'category',
  });
  const products = useQuery({
    queryKey: ['banner-link-products', term],
    queryFn: () => api.adminProducts({ query: term, status: 'ACTIVE', limit: 8 }),
    enabled: value.linkType === 'product' && term.trim().length >= 2,
  });

  const typeLabels: Record<BannerLinkType, string> = {
    none: t.refBannerLinkNone,
    product: t.refBannerLinkProduct,
    category: t.refBannerLinkCategory,
    catalog: t.refBannerLinkCatalog,
    search: t.refBannerLinkSearch,
  };

  return (
    <div className="aps-field adm-banner-link">
      <label className="aps-field">
        <span className="aps-field__label">{t.refBannerLink}</span>
        <select
          className="aps-field__input"
          value={value.linkType}
          onChange={(event) => {
            setTerm('');
            setChosen(null);
            onChange({ linkType: event.target.value as BannerLinkType, linkTarget: '' });
          }}
        >
          {(Object.keys(typeLabels) as BannerLinkType[]).map((type) => (
            <option key={type} value={type}>
              {typeLabels[type]}
            </option>
          ))}
        </select>
      </label>

      {value.linkType === 'product' && (
        <>
          {value.linkTarget !== '' && (
            <p className="aps-muted">
              {t.refBannerLinkChosen}:{' '}
              <strong>
                {chosen !== null && chosen.slug === value.linkTarget
                  ? `${chosen.sku} · ${pickLocalized(chosen.nameUz, chosen.nameRu, locale)}`
                  : value.linkTarget}
              </strong>
              {chosen !== null && chosen.slug === value.linkTarget && onFillFromProduct && (
                <>
                  {' '}
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      onFillFromProduct(chosen);
                    }}
                  >
                    {t.refBannerFillFromProduct}
                  </Button>
                </>
              )}
            </p>
          )}
          <input
            className="aps-field__input"
            aria-label={t.refBannerLinkProductSearch}
            placeholder={t.refBannerLinkProductSearch}
            value={term}
            onChange={(event) => {
              setTerm(event.target.value);
            }}
          />
          {term.trim().length >= 2 && products.data?.items.length === 0 && (
            <p className="aps-muted">{t.refBannerLinkNoProducts}</p>
          )}
          <ul className="adm-banner-link__results">
            {sortExactSkuFirst(products.data?.items ?? [], term).map((product) => (
              <li key={product.id}>
                <button
                  type="button"
                  className="adm-banner-link__result"
                  aria-pressed={value.linkTarget === product.slug}
                  onClick={() => {
                    setChosen(product);
                    onChange({ linkType: 'product', linkTarget: product.slug });
                  }}
                >
                  <span>{pickLocalized(product.nameUz, product.nameRu, locale)}</span>
                  <span className="aps-muted">
                    {product.sku} · {product.brandName} ·{' '}
                    {product.comingSoon
                      ? t.productComingSoonShort
                      : formatMoney(product.price, locale)}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </>
      )}

      {value.linkType === 'category' && (
        <select
          className="aps-field__input"
          aria-label={t.refBannerLinkCategory}
          value={value.linkTarget}
          onChange={(event) => {
            onChange({ linkType: 'category', linkTarget: event.target.value });
          }}
        >
          <option value="">{t.refBannerLinkPickCategory}</option>
          {flatten(categories.data ?? []).map((category) => (
            <option key={category.id} value={category.id}>
              {`${'— '.repeat(category.depth)}${category.name(locale)}`}
            </option>
          ))}
        </select>
      )}

      {value.linkType === 'search' && (
        <input
          className="aps-field__input"
          aria-label={t.refBannerLinkSearch}
          placeholder={t.refBannerLinkSearchPlaceholder}
          maxLength={BANNER_SEARCH_MAX}
          value={value.linkTarget}
          onChange={(event) => {
            onChange({ linkType: 'search', linkTarget: event.target.value });
          }}
        />
      )}
    </div>
  );
}
